import { useState, useCallback, useMemo, useEffect } from 'react';
import { message } from 'antd';
import debounce from 'lodash/debounce';
import moment from 'moment';
import {
  getApplicants,
  getBranchOffices,
  getChildCompanies,
  getCostsCenters,
  getCompanies,
} from '../../../services/general';

const buildInitialCompanyList = (reservation) => {
  const entries = [
    { business_name: reservation.company.business_name, id: reservation.company_id },
    reservation.outsourcing_id && { business_name: reservation.outsourcing.business_name, id: reservation.outsourcing_id },
    reservation.third_company_id && { business_name: reservation.third_company.business_name, id: reservation.third_company_id },
    reservation.invoice_company_id && { business_name: reservation.invoice_company.business_name, id: reservation.invoice_company_id },
  ].filter(Boolean);

  const seenCompanyIds = new Set();
  return entries.filter(e => {
    if (seenCompanyIds.has(e.id)) return false;
    seenCompanyIds.add(e.id);
    return true;
  });
};

export const useAdmissionCompanyData = ({ form, reservation }) => {
  const [loading, setLoading] = useState(false);
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [childCompanyLoading, setChildCompanyLoading] = useState(false);
  const [applicantList, setApplicantList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [childCompanies, setChildCompanies] = useState([]);
  const [costsCenters, setCostsCenters] = useState([]);
  const [branchOffices, setBranchOffices] = useState([]);
  const [showChildCompanies, setShowChildCompanies] = useState(false);

  const initializeCompanyFields = useCallback(() => {
    if (!reservation) return;

    setApplicantList([{ id: reservation.user_id, fullname: reservation.user_fullname }]);
    setCompanies(buildInitialCompanyList(reservation));

    if (reservation.cost_center?.id) {
      setCostsCenters([{ id: reservation.cost_center.id, name: reservation.cost_center.name }]);
    }

    if (reservation.child_company_id) {
      setChildCompanies([{ id: reservation.child_company.id, business_name: reservation.child_company.business_name }]);
      setShowChildCompanies(true);
    }

    form.setFieldsValue({
      user_id: reservation.user_id,
      company_id: reservation.company_id,
      outsourcing_id: reservation.outsourcing_id || undefined,
      third_company_id: reservation.third_company_id || undefined,
      invoice_company_id: reservation.invoice_company_id || undefined,
      cost_center_id: reservation.cost_center?.id || undefined,
      child_company_id: reservation.child_company_id || undefined,
      branch_office_id: reservation.branch_office_id,
      reservation_date: moment(reservation.reservation_date, 'YYYY-MM-DD'),
      observations: reservation.observations || '',
    });
  }, [form, reservation]);

  useEffect(() => {
    initializeCompanyFields();
  }, [initializeCompanyFields]);

  const fetchApplicants = useMemo(() => debounce(async (query) => {
    if (!query) return;
    setApplicantLoading(true);
    try {
      const response = await getApplicants({ query, limit: 50 });
      setApplicantList(response.data);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al buscar solicitante`, style: { marginTop: '45vh' } });
    } finally {
      setApplicantLoading(false);
    }
  }, 500), []);

  const fetchCompanies = useMemo(() => debounce(async (query) => {
    if (!query) return;
    setLoading(true);
    try {
      const response = await getCompanies({ query, branch_office_id: form.getFieldValue('branch_office_id') });
      setCompanies(response.data);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al buscar empresa`, style: { marginTop: '45vh' } });
    } finally {
      setLoading(false);
    }
  }, 500), [form]);

  const fetchBranchOffices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBranchOffices();
      setBranchOffices(response);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al cargar sedes`, style: { marginTop: '45vh' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCostsCenters = useCallback(async (companyId) => {
    try {
      const response = await getCostsCenters({ companyId, limit: 50 });
      setCostsCenters(response);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al cargar centros de costos", style: { marginTop: '45vh' } });
    }
  }, []);

  const fetchChildCompanies = useCallback(async (name) => {
    setChildCompanyLoading(true);
    try {
      const companyId = form.getFieldValue('company_id');
      const response = await getChildCompanies({ companyId, limit: 50, name });
      setChildCompanies(response);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al buscar sub empresa`, style: { marginTop: '45vh' } });
    } finally {
      setChildCompanyLoading(false);
    }
  }, [form]);

  const searchChildCompanies = useMemo(() => debounce(async (name) => {
    fetchChildCompanies(name);
  }, 500), [fetchChildCompanies]);

  const handleCompanyChange = useCallback((value, option) => {
    if (!option) {
      setShowChildCompanies(false);
      setCostsCenters([]);
      return;
    }
    if (option.company?.is_provider) {
      setShowChildCompanies(true);
      fetchChildCompanies('');
    }
    if (option.company?.has_cost_centers) {
      fetchCostsCenters(value);
    } else {
      setCostsCenters([]);
    }
  }, [fetchChildCompanies, fetchCostsCenters]);

  return {
    loading,
    applicantLoading,
    childCompanyLoading,
    applicantList,
    companies,
    setCompanies,
    childCompanies,
    costsCenters,
    branchOffices,
    showChildCompanies,
    fetchApplicants,
    fetchCompanies,
    fetchBranchOffices,
    searchChildCompanies,
    handleCompanyChange,
    reinitializeCompanyFields: initializeCompanyFields,
  };
};
